import type { SurveysOnboardingOperationsStage } from "@/lib/evaluation-dashboard-onboarding"

export type LeoMissionStatus = "watching" | "ready" | "needsYou"

export type LeoMissionStep = {
  id: string
  label: string
  done: boolean
}

export type SurveysDashboardLeoInsight = {
  title: string
  description: string
  severity: "info" | "warning"
  askLeoPrompt: string
  missionStatus: LeoMissionStatus
  steps: readonly LeoMissionStep[]
}

export const LEO_MISSION_STATUS_META: Record<
  LeoMissionStatus,
  { label: string; tone: "info" | "warning" | "success"; icon: string }
> = {
  watching: { label: "Watching", tone: "info", icon: "fa-signal-stream" },
  ready: { label: "Ready", tone: "success", icon: "fa-circle-check" },
  needsYou: { label: "Needs you", tone: "warning", icon: "fa-flag" },
}

const SETUP_STEP_INSIGHTS: SurveysDashboardLeoInsight[] = [
  {
    title: "Start with term dates",
    description: "Add dates and academic year so the rest of setup can be scoped to this term.",
    severity: "info",
    askLeoPrompt: "How do I set up my first term dates and academic year in Surveys?",
    missionStatus: "ready",
    steps: [
      { id: "dates", label: "Confirm term dates", done: false },
      { id: "courses", label: "Connect course list", done: false },
      { id: "survey", label: "Create first survey", done: false },
    ],
  },
  {
    title: "Connect course offerings next",
    description: "Select the courses that should receive student and faculty evaluations.",
    severity: "info",
    askLeoPrompt: "Which courses should I connect before I create the first survey?",
    missionStatus: "ready",
    steps: [
      { id: "dates", label: "Confirm term dates", done: true },
      { id: "courses", label: "Connect course list", done: false },
      { id: "survey", label: "Create first survey", done: false },
    ],
  },
  {
    title: "Create the first survey",
    description: "Import a template or name a new survey for this term.",
    severity: "info",
    askLeoPrompt: "What should I do next to create the first survey in this term?",
    missionStatus: "ready",
    steps: [
      { id: "dates", label: "Confirm term dates", done: true },
      { id: "courses", label: "Connect course list", done: true },
      { id: "survey", label: "Create first survey", done: false },
    ],
  },
  {
    title: "Set open dates last",
    description: "Schedule each course before collection can start.",
    severity: "info",
    askLeoPrompt: "How do I schedule evaluations after the first survey exists?",
    missionStatus: "needsYou",
    steps: [
      { id: "survey", label: "Create first survey", done: true },
      { id: "schedule", label: "Set open dates", done: false },
      { id: "collect", label: "Start collection", done: false },
    ],
  },
]

const SCHEDULE_INSIGHT: SurveysDashboardLeoInsight = {
  title: "Draft schedules still open",
  description: "Set open dates on draft courses before collection starts.",
  severity: "warning",
  askLeoPrompt: "Which courses still need evaluation schedules in the current term?",
  missionStatus: "needsYou",
  steps: [
    { id: "courses", label: "Connect course list", done: true },
    { id: "survey", label: "Create first survey", done: true },
    { id: "schedule", label: "Set open dates", done: false },
  ],
}

const COLLECTING_INSIGHT: SurveysDashboardLeoInsight = {
  title: "Collection is live",
  description: "Watch which courses are stalling so you can nudge respondents.",
  severity: "info",
  askLeoPrompt: "Which courses have low response rates in the current term?",
  missionStatus: "watching",
  steps: [
    { id: "schedule", label: "Schedule courses", done: true },
    { id: "open", label: "Open collection", done: true },
    { id: "nudge", label: "Nudge low responders", done: false },
  ],
}

const FULL_INSIGHT: SurveysDashboardLeoInsight = {
  title: "Last term is ready to review",
  description: "Compare response rates and scores before you copy schedules forward.",
  severity: "info",
  askLeoPrompt: "Summarize last term response rates and flag courses below threshold.",
  missionStatus: "ready",
  steps: [
    { id: "closed", label: "Collection closed", done: true },
    { id: "compare", label: "Compare last term", done: false },
    { id: "copy", label: "Copy schedules forward", done: false },
  ],
}

export function surveysDashboardLeoInsight(
  stage: SurveysOnboardingOperationsStage,
  setupStepIndex = 0,
): SurveysDashboardLeoInsight {
  if (stage === "hidden") {
    const index = Math.min(Math.max(setupStepIndex, 0), SETUP_STEP_INSIGHTS.length - 1)
    return SETUP_STEP_INSIGHTS[index]!
  }
  if (stage === "guided" || stage === "ready") return SCHEDULE_INSIGHT
  if (stage === "collecting") return COLLECTING_INSIGHT
  return FULL_INSIGHT
}
